import * as React from "react";
import { cva } from "class-variance-authority";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

/** @param {{ children?: React.ReactNode }} props */
const ToastProvider = ({ children }) => children;
ToastProvider.displayName = "ToastProvider";

/** @type {React.ForwardRefRenderFunction<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>} */
const ToastViewportImpl = ({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "pointer-events-none fixed inset-x-0 top-0 z-[200] flex max-h-[100dvh] flex-col gap-3 overflow-y-auto px-3 pb-4 pt-[max(0.75rem,env(safe-area-inset-top))] sm:left-auto sm:right-4 sm:w-[min(420px,calc(100vw-2rem))] sm:px-0 sm:pt-4",
      className
    )}
    {...props}
  />
);
const ToastViewport = React.forwardRef(ToastViewportImpl);
ToastViewport.displayName = "ToastViewport";

const toastVariants = cva(
  "group pointer-events-auto relative flex w-full items-start gap-3 overflow-hidden border-2 bg-white p-4 pr-11 text-cdi-carbon shadow-[5px_5px_0_rgba(38,43,92,0.18)] data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-right-full data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=closed]:fill-mode-forwards data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-right-full",
  {
    variants: {
      variant: {
        default: "border-cdi-blue",
        info: "border-cdi-blue",
        success: "border-emerald-600",
        warning: "border-amber-500",
        error: "border-red-600",
        destructive: "border-red-600",
      },
    },
    defaultVariants: {
      variant: "info",
    },
  }
);

/**
 * @typedef {React.HTMLAttributes<HTMLDivElement> & {
 *   variant?: "default" | "info" | "success" | "warning" | "error" | "destructive",
 *   open?: boolean,
 *   onOpenChange?: (open: boolean) => void,
 * }} ToastProps
 */

/** @type {React.ForwardRefRenderFunction<HTMLDivElement, ToastProps>} */
const ToastImpl = ({ className, variant, open = true, onOpenChange: _onOpenChange, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(toastVariants({ variant }), className)}
      data-state={open ? "open" : "closed"}
      {...props}
    />
  );
};
const Toast = React.forwardRef(ToastImpl);
Toast.displayName = "Toast";

/** @type {React.ForwardRefRenderFunction<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>} */
const ToastActionImpl = ({ className, ...props }, ref) => (
  <button
    type="button"
    ref={ref}
    className={cn(
      "mt-3 inline-flex min-h-9 items-center justify-center border-2 border-current bg-transparent px-3 text-xs font-black uppercase tracking-wide transition-colors hover:bg-cdi-blue hover:text-white focus:outline-none focus:ring-2 focus:ring-cdi-yellow focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
      className
    )}
    {...props}
  />
);
const ToastAction = React.forwardRef(ToastActionImpl);
ToastAction.displayName = "ToastAction";

/** @type {React.ForwardRefRenderFunction<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>} */
const ToastCloseImpl = ({ className, ...props }, ref) => (
  <button
    ref={ref}
    className={cn(
      "absolute right-2.5 top-2.5 p-1.5 text-cdi-carbon/55 transition-colors hover:bg-cdi-blue/10 hover:text-cdi-blue focus:outline-none focus:ring-2 focus:ring-cdi-yellow focus:ring-offset-1",
      className
    )}
    toast-close=""
    {...props}
  >
    <X className="h-4 w-4" aria-hidden="true" />
  </button>
);
const ToastClose = React.forwardRef(ToastCloseImpl);
ToastClose.displayName = "ToastClose";

/** @type {React.ForwardRefRenderFunction<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>} */
const ToastTitleImpl = ({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm font-black text-cdi-blue", className)}
    {...props}
  />
);
const ToastTitle = React.forwardRef(ToastTitleImpl);
ToastTitle.displayName = "ToastTitle";

/** @type {React.ForwardRefRenderFunction<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>} */
const ToastDescriptionImpl = ({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm font-medium leading-relaxed text-cdi-carbon/75", className)}
    {...props}
  />
);
const ToastDescription = React.forwardRef(ToastDescriptionImpl);
ToastDescription.displayName = "ToastDescription";

export {
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
};
